import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCCPOPRPSievesContext } from '../context/CCPOPRPSievesContext';
import moment from 'moment';
import '../styles/ccpOprpSieves.css';

const SummaryReporting: React.FC = () => {
    const { state } = useCCPOPRPSievesContext();
    const navigate = useNavigate();
    const session = state.sessionData;
    const completedCycles = state.completedCycles;

    const [filterCycle, setFilterCycle] = useState<string>('All');
    const isCCP = (session?.parameterVerification || state.parameterVerification) === "CCP & OPRP";

    // Aggregate metrics
    const totalCycles = completedCycles.length;
    let totalChecks = 0;
    let okayChecks = 0;
    let notOkayChecks = 0;
    let nonOperationalCycles = 0;
    const deviations: any[] = [];

    completedCycles.forEach(c => {
        if (c.acceptanceResponse && c.acceptanceResponse !== "OK") {
            nonOperationalCycles++;
        }
        if (c.checkpoints && c.checkpoints.length > 0) {
            c.checkpoints.forEach(cp => {
                totalChecks++;
                if (cp.criteria === "Okay") okayChecks++;
                else if (cp.criteria === "Not Okay") {
                    notOkayChecks++;
                    deviations.push({
                        cycle: c.cycle,
                        name: cp.checkpointName,
                        category: cp.category,
                        defectRemarks: cp.defectRemarks || "N/A",
                        actionTaken: cp.actionTaken || "Pending",
                        department: cp.notifiedDepartment || "Production",
                        status: cp.deviationStatus || "Raised"
                    });
                }
            });
        } else if (c.sievesItems && c.sievesItems.length > 0) {
            c.sievesItems.forEach(item => {
                totalChecks++;
                if (item.criteria === "Okay") okayChecks++;
                else if (item.criteria === "Not Okay") {
                    notOkayChecks++;
                    deviations.push({
                        cycle: c.cycle,
                        name: item.description,
                        category: item.plantGroup,
                        defectRemarks: item.defectRemarks || "N/A",
                        actionTaken: "Inspection Action",
                        department: "Production",
                        status: "Logged"
                    });
                }
            });
        }
    });

    const overallScore = totalChecks > 0 ? Math.round((okayChecks / totalChecks) * 100) : (totalCycles > 0 ? 100 : 0);

    // const handlePrint = () => {
    //     window.print();
    // };

    // const handleExportCSV = () => {
    //     const rows: string[][] = [];
    //     rows.push(["Cycle", "Type", "Item Name", "Result", "Remarks", "Action Taken", "Department", "Status"]);

    //     completedCycles.forEach(c => {
    //         if (c.acceptanceResponse && c.acceptanceResponse !== "OK") {
    //             rows.push([c.cycle, "Acceptance", "Line Operation Status", c.acceptanceResponse, c.acceptanceReason || "", "", "", "Logged"]);
    //         }
    //         if (c.checkpoints) {
    //             c.checkpoints.forEach(cp => {
    //                 rows.push([c.cycle, cp.category, cp.checkpointName, cp.criteria, cp.defectRemarks || "", cp.actionTaken || "", cp.notifiedDepartment || "", cp.deviationStatus || ""]);
    //             });
    //         }
    //         if (c.sievesItems) {
    //             c.sievesItems.forEach(item => {
    //                 rows.push([c.cycle, item.plantGroup, item.description, item.criteria, item.defectRemarks || "", "", "Production", ""]);
    //             });
    //         }
    //     });

    //     const csvContent = "data:text/csv;charset=utf-8," + rows.map(e => e.map(val => `"${val.replace(/"/g, '""')}"`).join(",")).join("\n");
    //     const encodedUri = encodeURI(csvContent);
    //     const link = document.createElement("a");
    //     link.setAttribute("href", encodedUri);
    //     link.setAttribute("download", `Quality_Tour_Summary_${(session?.parameterVerification || 'Tour').replace(/\s+/g, '_')}_${moment().format('YYYYMMDD')}.csv`);
    //     document.body.appendChild(link);
    //     link.click();
    //     document.body.removeChild(link);
    // };

    // Render Matrix Summary table matching checklist.js
    const renderMatrixSummaryTable = (checkpointsList: any[]) => {
        const map: Record<string, {
            name: string;
            fe1: string;
            fe2: string;
            nfe1: string;
            nfe2: string;
            ss1: string;
            ss2: string;
            mdSens: string;
            mdSensVal: string;
        }> = {};

        checkpointsList.forEach(cp => {
            const full = cp.checkpointName || "";
            const parts = full.split(" - ");
            const parentName = parts[0] || "Checkpoint";

            let mapKey = parentName;
            let subLabel = "";
            if (parts.length >= 3) {
                mapKey = `${parentName} - ${parts[1]}`;
                subLabel = parts.slice(2).join(" - ");
            } else {
                mapKey = `${parentName} - Row 1`;
                subLabel = parts.slice(1).join(" - ");
            }

            if (!map[mapKey]) {
                map[mapKey] = {
                    name: mapKey,
                    fe1: "OK",
                    fe2: "OK",
                    nfe1: "OK",
                    nfe2: "OK",
                    ss1: "OK",
                    ss2: "OK",
                    mdSens: "OK",
                    mdSensVal: "N/A"
                };
            }

            let val = cp.criteria === "Okay" ? "OK" : "Not Okay";
            if (cp.deviationStatus === "Closed") {
                val = "OK";
            }
            const labelLower = subLabel.toLowerCase();

            if (labelLower.includes("fe") && !labelLower.includes("nfe") && labelLower.includes("1st")) map[mapKey].fe1 = val;
            else if (labelLower.includes("fe") && !labelLower.includes("nfe") && labelLower.includes("2nd")) map[mapKey].fe2 = val;
            else if (labelLower.includes("nfe") && labelLower.includes("1st")) map[mapKey].nfe1 = val;
            else if (labelLower.includes("nfe") && labelLower.includes("2nd")) map[mapKey].nfe2 = val;
            else if (labelLower.includes("ss") && labelLower.includes("1st")) map[mapKey].ss1 = val;
            else if (labelLower.includes("ss") && labelLower.includes("2nd")) map[mapKey].ss2 = val;
            else if (labelLower.includes("md") || labelLower.includes("sensitivity")) {
                map[mapKey].mdSens = val;
                map[mapKey].mdSensVal = cp.mdSensitivityValue || "N/A";
            }
        });

        const rows = Object.values(map);

        return (
            <div className="bs-table-container" style={{ marginTop: 10 }}>
                <table className="bs-table">
                    <thead>
                        <tr style={{ background: '#cbd5e1' }}>
                            <th style={{ border: '1px solid #94a3b8', padding: '6px' }}>Checkpoint</th>
                            <th style={{ border: '1px solid #94a3b8', padding: '6px', textAlign: 'center' }}>FE-1</th>
                            <th style={{ border: '1px solid #94a3b8', padding: '6px', textAlign: 'center' }}>FE-2</th>
                            <th style={{ border: '1px solid #94a3b8', padding: '6px', textAlign: 'center' }}>NFE-1</th>
                            <th style={{ border: '1px solid #94a3b8', padding: '6px', textAlign: 'center' }}>NFE-2</th>
                            <th style={{ border: '1px solid #94a3b8', padding: '6px', textAlign: 'center' }}>SS-1</th>
                            <th style={{ border: '1px solid #94a3b8', padding: '6px', textAlign: 'center' }}>SS-2</th>
                            <th style={{ border: '1px solid #94a3b8', padding: '6px', textAlign: 'center' }}>MD Sens</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i}>
                                <td style={{ border: '1px solid #cbd5e1', padding: '6px', fontWeight: 600 }}>{r.name}</td>
                                <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center' }} className={r.fe1 === 'OK' ? 'ok-text' : 'not-ok-text'}>{r.fe1}</td>
                                <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center' }} className={r.fe2 === 'OK' ? 'ok-text' : 'not-ok-text'}>{r.fe2}</td>
                                <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center' }} className={r.nfe1 === 'OK' ? 'ok-text' : 'not-ok-text'}>{r.nfe1}</td>
                                <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center' }} className={r.nfe2 === 'OK' ? 'ok-text' : 'not-ok-text'}>{r.nfe2}</td>
                                <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center' }} className={r.ss1 === 'OK' ? 'ok-text' : 'not-ok-text'}>{r.ss1}</td>
                                <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center' }} className={r.ss2 === 'OK' ? 'ok-text' : 'not-ok-text'}>{r.ss2}</td>
                                <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center' }} className={r.mdSens === 'OK' ? 'ok-text' : 'not-ok-text'}>
                                    {r.mdSensVal !== "N/A" ? r.mdSensVal : r.mdSens}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const filteredCycles = completedCycles.filter(c => filterCycle === 'All' || c.cycle === filterCycle);

    return (
        <div id="rajpuraQualityDashboard">
            <main className="plan-tour-content">
                <div id="checklist-main-container">
                    
                    {/* 1. Header Toolbar */}
                    <div className="plan-tour-header" style={{ marginBottom: 20 }}>
                        <div className="tour-header-info-wrapper">
                            <h3 className="tour-header-title">
                                {isCCP ? "CCP & OPRP SUMMARY REPORT (RAJPURA)" : "SIEVES & MAGNETS SUMMARY REPORT (RAJPURA)"}
                            </h3>
                            
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <div className="tour-header-meta">
                                <span className="badge" id="shiftBadge">{session?.shift || state.selectedShift || "Shift 1"}</span>
                                <span className="tour-date" id="currentDay">{moment().format("DD/MM/YYYY")}</span>
                            </div>
                                {/* <button
                                    type="button"
                                    onClick={handleExportCSV}
                                    className="bs-btn bs-btn-outline-primary"
                                    style={{ fontSize: 12, padding: '6px 12px' }}
                                >
                                    📥 Export CSV
                                </button>
                                <button
                                    type="button"
                                    onClick={handlePrint}
                                    className="bs-btn bs-btn-outline-primary"
                                    style={{ fontSize: 12, padding: '6px 12px' }}
                                >
                                    🖨️ Print
                                </button> */}
                                <button
                                    type="button"
                                    onClick={() => navigate('/home')}
                                    className="bs-btn bs-btn-primary"
                                    style={{ fontSize: 12, padding: '6px 14px' }}
                                >
                                    Dashboard
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* 2. Tour Metadata Card */}
                    <div className="bs-card bs-card-secondary" style={{ marginBottom: 20 }}>
                        <div className="bs-card-header" style={{ padding: '12px 20px', borderBottom: '1px solid #e2e8f0' }}>
                            <h4 className="bs-card-title" style={{ fontSize: 16, margin: 0 }}>
                                Inspection Tour Details
                            </h4>
                            {/* <span className="badge" style={{ backgroundColor: '#e0f2fe', color: '#0369a1', fontWeight: 600 }}>
                                ID: {state.currentTourId || "Offline Session"}
                            </span> */}
                        </div>
                        <div className="bs-card-body" style={{ padding: '16px 20px' }}>
                            <div className="tour-cyle-start-info" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                                <div><strong>Plant / Site:</strong> {session?.site || "Rajpura"}</div>
                                <div><strong>Line No:</strong> {session?.line || "Line-1"}</div>
                                <div><strong>Verification:</strong> {session?.parameterVerification || state.parameterVerification}</div>
                                <div><strong>Shift:</strong> {session?.shift || "Shift 1"}</div>
                                <div><strong>Conducted Date:</strong> {session?.tourStartDate ? moment(session.tourStartDate).format("DD-MM-YYYY hh:mm A") : moment().format("DD-MM-YYYY hh:mm A")}</div>
                                <div><strong>QA Executive:</strong> {session?.qaExecutive || "QA Team"}</div>
                                <div><strong>Shift Incharge:</strong> {session?.productionIncharge || "Production Team"}</div>
                                <div><strong>Variety / Frequency:</strong> {session?.productVariety || session?.frequency || "Standard"}</div>
                            </div>
                        </div>
                    </div>

                    {/* 3. KPI Metrics Summary Cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
                        <div className="bs-card" style={{ padding: 14, textAlign: 'center', borderLeft: '4px solid #2563eb' }}>
                            <span style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700, display: 'block', marginBottom: 4 }}>
                                Total Cycles
                            </span>
                            <strong style={{ fontSize: 24, color: '#0f172a' }}>{totalCycles}</strong>
                        </div>
                        <div className="bs-card" style={{ padding: 14, textAlign: 'center', borderLeft: '4px solid #16a34a' }}>
                            <span style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700, display: 'block', marginBottom: 4 }}>
                                Compliance Score
                            </span>
                            <strong style={{ fontSize: 24, color: '#16a34a' }}>{overallScore}%</strong>
                        </div>
                        <div className="bs-card" style={{ padding: 14, textAlign: 'center', borderLeft: '4px solid #0891b2' }}>
                            <span style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700, display: 'block', marginBottom: 4 }}>
                                Total Checks Passed
                            </span>
                            <strong style={{ fontSize: 24, color: '#0891b2' }}>{okayChecks} / {totalChecks}</strong>
                        </div>
                        <div className="bs-card" style={{ padding: 14, textAlign: 'center', borderLeft: '4px solid #dc2626' }}>
                            <span style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700, display: 'block', marginBottom: 4 }}>
                                Deviations / Not OK
                            </span>
                            <strong style={{ fontSize: 24, color: deviations.length > 0 ? '#dc2626' : '#16a34a' }}>
                                {deviations.length}
                            </strong>
                        </div>
                    </div>

                    {/* 4. Cycle Filter Tabs if multiple cycles */}
                    {completedCycles.length > 1 && (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 15, overflowX: 'auto', paddingBottom: 4 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>Filter Cycle:</span>
                            <button
                                type="button"
                                onClick={() => setFilterCycle('All')}
                                className={`tab-header-btn ${filterCycle === 'All' ? 'active' : ''}`}
                                style={{ padding: '6px 14px', fontSize: 12 }}
                            >
                                All ({completedCycles.length})
                            </button>
                            {completedCycles.map(c => (
                                <button
                                    key={c.cycle}
                                    type="button"
                                    onClick={() => setFilterCycle(c.cycle)}
                                    className={`tab-header-btn ${filterCycle === c.cycle ? 'active' : ''}`}
                                    style={{ padding: '6px 14px', fontSize: 12 }}
                                >
                                    {c.cycle}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* 5. Detailed Breakdown by Cycle */}
                    <div className="tour-cycle-card-panel-lists">
                        {filteredCycles.map((cRecord, idx) => {
                            const status = cRecord.status || (cRecord.hasDeviations ? "Pending Production Action" : "Completed");
                            const cDeviations = (cRecord.checkpoints || []).filter(cp => cp.criteria === "Not Okay");

                            return (
                                <div key={idx} className="bs-card bs-card-secondary tour-cycle-panel" style={{ marginBottom: 20 }}>
                                    <div className="bs-card-header" style={{ padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <h4 className="bs-card-title" style={{ margin: 0, fontSize: 16 }}>{cRecord.cycle}</h4>
                                            <span
                                                className="badge"
                                                style={{
                                                    backgroundColor: status === "Completed" ? "#dcfce7" : "#ffedd5",
                                                    color: status === "Completed" ? "#16a34a" : "#ea580c",
                                                    fontWeight: 600,
                                                    padding: '4px 8px',
                                                    borderRadius: 4,
                                                    fontSize: 11
                                                }}
                                            >
                                                {status}
                                            </span>
                                        </div>
                                        <span className="tour-date" style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                                            {cRecord.cycleDate ? moment(cRecord.cycleDate).format("DD/MM/YYYY hh:mm A") : moment().format("DD/MM/YYYY")}
                                        </span>
                                    </div>

                                    <div className="bs-card-body" style={{ padding: '16px 20px' }}>
                                        {/* Cycle Metadata Info */}
                                        <div className="tour-cyle-start-info" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8, marginBottom: 15 }}>
                                            <div><strong>Product / Item:</strong> {cRecord.productName || session?.productVariety || "N/A"}</div>
                                            <div><strong>Shift Executive:</strong> {cRecord.executiveName || session?.productionIncharge || "N/A"}</div>
                                            <div><strong>Location:</strong> {cRecord.location || session?.line || "Rajpura"}</div>
                                            <div><strong>Response Status:</strong> {cRecord.acceptanceResponse || "OK"}</div>
                                        </div>

                                        {/* Table Summary */}
                                        {isCCP ? (
                                            cRecord.checkpoints && cRecord.checkpoints.length > 0 ? (
                                                renderMatrixSummaryTable(cRecord.checkpoints)
                                            ) : (
                                                <div style={{ padding: 12, background: '#fee2e2', borderRadius: 6, color: '#991b1b', fontSize: 13 }}>
                                                    ⚠️ {cRecord.acceptanceResponse || "Line Not Operational"} ({cRecord.acceptanceReason || "Shutdown"})
                                                </div>
                                            )
                                        ) : (
                                            cRecord.sievesItems && (
                                                <div className="bs-table-container" style={{ marginTop: 10 }}>
                                                    <table className="bs-table">
                                                        <thead>
                                                            <tr style={{ background: '#cbd5e1' }}>
                                                                <th style={{ border: '1px solid #94a3b8', padding: '6px' }}>Sieve Description</th>
                                                                <th style={{ border: '1px solid #94a3b8', padding: '6px', textAlign: 'center' }}>Plant Group</th>
                                                                <th style={{ border: '1px solid #94a3b8', padding: '6px', textAlign: 'center' }}>Frequency</th>
                                                                <th style={{ border: '1px solid #94a3b8', padding: '6px', textAlign: 'center' }}>Status</th>
                                                                <th style={{ border: '1px solid #94a3b8', padding: '6px' }}>Defect Remarks</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {cRecord.sievesItems.map((sItem, sIdx) => (
                                                                <tr key={sIdx}>
                                                                    <td style={{ border: '1px solid #cbd5e1', padding: '6px', fontWeight: 600 }}>{sItem.description}</td>
                                                                    <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center' }}>{sItem.plantGroup}</td>
                                                                    <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center' }}>{sItem.frequency}</td>
                                                                    <td style={{ border: '1px solid #cbd5e1', padding: '6px', textAlign: 'center' }} className={sItem.criteria === 'Okay' ? 'ok-text' : 'not-ok-text'}>
                                                                        <strong>{sItem.criteria}</strong>
                                                                    </td>
                                                                    <td style={{ border: '1px solid #cbd5e1', padding: '6px' }}>{sItem.defectRemarks || "-"}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )
                                        )}

                                        {/* Deviations Panel if any */}
                                        {cDeviations.length > 0 && (
                                            <div className="deviation-workflow-panel" style={{ marginTop: 15, padding: 12, border: '1px solid #fed7aa', background: '#fff7ed', borderRadius: 6 }}>
                                                <h5 style={{ margin: '0 0 10px 0', color: '#c2410c', fontWeight: 'bold' }}>
                                                    ⚠️ Registered Quality Deviations ({cDeviations.length})
                                                </h5>
                                                {cDeviations.map((dev, dIdx) => (
                                                    <div key={dIdx} style={{ padding: 8, background: '#fff', border: '1px solid #fde68a', borderRadius: 4, marginBottom: 6 }}>
                                                        <div style={{ fontWeight: 600, fontSize: 13 }}>{dev.checkpointName}</div>
                                                        <div style={{ fontSize: 12, color: '#dc2626' }}><strong>Remarks:</strong> {dev.defectRemarks || "No remarks"}</div>
                                                        <div style={{ fontSize: 12, color: '#475569' }}><strong>Action Plan:</strong> {dev.actionTaken || "Pending Production Action"}</div>
                                                        <div style={{ fontSize: 11, color: '#0369a1', marginTop: 2 }}><strong>Status:</strong> {dev.deviationStatus || "Raised"}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default SummaryReporting;
